/**
 * Notifications drawer (screenshots/53): pure grouping + a small Zustand store fed by the org channel.
 * First paint: hydrate() from snapshot:notifications (Worker: KV notifications:{userId} + read state in ACCOUNTS).
 * Reads are optimistic and saved with POST /api/dashboard/notifications/read.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";

export type NotificationKind = "forecast" | "brief" | "mention" | "agenda" | "committee" | "filing" | "rollcall";
export interface AppNotification { id: string; kind: NotificationKind; title: string; detail: string; at: string /* ISO */; read: boolean; href: string; direction?: "up" | "down" /* forecast */ }

export const ICON: Record<NotificationKind, string> = {
  forecast: "trending-down", brief: "sparkles", mention: "at-sign", agenda: "calendar", committee: "scroll-text", filing: "file-plus", rollcall: "gavel",
};

/** Today / Earlier, newest first, in the viewer's time zone. */
export function groupByDay(items: AppNotification[], now: Date, tz = "America/Chicago") {
  const day = (d: Date) => d.toLocaleDateString("en-US", { timeZone: tz });
  const today = day(now);
  const sorted = [...items].sort((a, b) => b.at.localeCompare(a.at));
  return { today: sorted.filter((n) => day(new Date(n.at)) === today), earlier: sorted.filter((n) => day(new Date(n.at)) !== today) };
}

export const unreadCount = (items: AppNotification[]) => items.filter((n) => !n.read).length;

interface NotificationsState {
  items: AppNotification[];
  sample: boolean;                     // fixture data until the producer exists ("Sample notifications")
  hydrate: (items: AppNotification[], sample?: boolean) => void;
  filter: "all" | "mentions";
  open: boolean;
  add: (n: AppNotification) => void;
  markAllRead: () => void;
  markRead: (id: string) => void;
  setFilter: (f: "all" | "mentions") => void;
  setOpen: (o: boolean) => void;
}

const saveRead = (body: { ids: string[] } | { all: true }) => {
  if (typeof location === "undefined") return;
  void fetch("/api/dashboard/notifications/read", { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }).catch(() => {});
};

export const notificationsStore = createStore<NotificationsState>()((set, get) => ({
  items: [], sample: false, filter: "all", open: false,
  hydrate: (items, sample = false) => set({ items, sample }),
  add: (n) => set((s) => (s.items.some((x) => x.id === n.id) ? s : { items: [n, ...s.items] })),   // idempotent on replay
  markAllRead: () => {
    if (!get().items.some((n) => !n.read)) return;
    set((s) => ({ items: s.items.map((n) => ({ ...n, read: true })) }));
    saveRead({ all: true });
  },
  markRead: (id) => {
    if (!get().items.some((n) => n.id === id && !n.read)) return;
    set((s) => ({ items: s.items.map((n) => (n.id === id ? { ...n, read: true } : n)) }));
    saveRead({ ids: [id] });
  },
  setFilter: (filter) => set({ filter }),
  setOpen: (open) => set({ open }),
}));
export const useNotifications = <U,>(sel: (s: NotificationsState) => U) => useStore(notificationsStore, sel);

/** Open the drawer from anywhere: dispatchEvent(new Event("notifications:open")) or a #notifications link (the shell listens). */
export const openNotifications = () => notificationsStore.getState().setOpen(true);
